import React, { useEffect, useRef, useState } from "react";
import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";
import UtilityHelper from "../utilities/UtilityHelper";
import Scaffold from "../../widget/layouts/Scaffold";
import AppTranslation from "../app_translation/AppTranslation";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { RootState } from "../../libraries/state_reducers/store";
import {
  provideFileEdited,
  provideFileString,
} from "../../libraries/state_reducers/service/ImageEditorReducer";
import AppTheme from "../app_config/AppTheme";
import { IconCircleFilledIcon } from "../../widget/common/ui/Icon";
import ShowToast from "../../widget/common/ui/Toast";
import { ResponsiveDeviceType } from "../../widget/layouts/ScreenMedia";
import Loader from "../../widget/common/ui/Loader";
import Center from "../../widget/common/ui/Center";
import { removeModal } from "../../libraries/state_reducers/widget/ModalsReducer";
import ModalKeys from "../../const/ModalKeys";
import StorageKeys from "../../const/StorageKeys";
import CacheStorage from "../storage/CacheStorage";

interface VideoTrimmerProps {
  src: string;
  thumbnailCount?: number;
  width?: number;
  height?: number;
}

const ffmpeg = new FFmpeg();

const VideoTrimmer: React.FC<VideoTrimmerProps> = ({
  src,
  thumbnailCount = 10,
}) => {
  const type: number = ResponsiveDeviceType();
  const dispatch = useDispatch();

  const fileStringDetailSelector = useSelector(
    (state: RootState) => state.imageEditor.fileStringDetail
  );

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const trimmedBlobRef = useRef<Blob | null>(null);

  /** State */
  const [duration, setDuration] = useState(0);
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  //const [start, setStart] = useState(0);
  //const [end, setEnd] = useState(0);
  //const [processing, setProcessing] = useState(false);
  //const [outputUrl, setOutputUrl] = useState<string | null>(null);

  //const [base64, setBase64] = useState<string | null>(null);

  const [range, setRange] = useState<[number, number]>([0, 1]);

  /* --------------------------------------------------------
     Load Video Duration
  -------------------------------------------------------- */
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handler = () => {
      setDuration(video.duration);
      //setEnd(video.duration);
      setRange([0, video.duration]);
    };

    video.addEventListener("loadedmetadata", handler);
    return () => video.removeEventListener("loadedmetadata", handler);
  }, [src]);

  /* --------------------------------------------------------
     Generate Thumbnails
  -------------------------------------------------------- */
  useEffect(() => {
    if (!duration) return;
    const video = videoRef.current!;
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    const temp: string[] = [];

    const capture = (time: number, index: number) =>
      new Promise<void>((resolve) => {
        video.currentTime = time;
        const onSeeked = () => {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          temp[index] = canvas.toDataURL("image/jpeg");
          resolve();
          video.removeEventListener("seeked", onSeeked);
        };
        video.addEventListener("seeked", onSeeked);
      });

    (async () => {
      for (let i = 0; i < thumbnailCount; i++) {
        const t = (duration / (thumbnailCount - 1)) * i;
        await capture(t, i);
      }
      setThumbnails(temp);
    })();
  }, [duration, thumbnailCount]);

  /* --------------------------------------------------------
     Play Trimmed Section
  -------------------------------------------------------- */
  const playTrim = () => {
    const video = videoRef.current!;
    //video.currentTime = start;
    video.currentTime = range[0];
    video.play();

    console.log(range[0], range[1]);

    const stop = () => {
      //if (video.currentTime >= end) {
      if (video.currentTime >= range[1]) {
        video.pause();
        video.removeEventListener("timeupdate", stop);
      }
    };

    video.addEventListener("timeupdate", stop);
  };

  /* --------------------------------------------------------
     Export Trim via FFmpeg WASM
  -------------------------------------------------------- */
  const exportTrim = async () => {
    //setProcessing(true);
    //setOutputUrl(null);

    trimmedBlobRef.current = null;
    //setBase64(null);
    // Lazy load FFmpeg only once
    if (!ffmpeg.loaded) {
      await ffmpeg.load();
    }

    // Read video file from URL (public / assets / blob)
    const inputData = await fetchFile(src);
    ffmpeg.writeFile("input.mp4", inputData);

    const startFixed = range[0].toFixed(2); //start.toFixed(2);
    const lengthFixed = (range[1] - range[0]).toFixed(2); //(end - start).toFixed(2);

    console.log(range[0], range[1]);

    // Run ffmpeg trim
    await ffmpeg.exec([
      "-i",
      "input.mp4",
      "-ss",
      startFixed,
      "-t",
      lengthFixed,
      "-c:v",
      "copy",
      "-c:a",
      "copy",
      "output.mp4",
    ]);

    // Read output
    const data: any = await ffmpeg.readFile("output.mp4");
    const blob = new Blob([data], { type: "video/mp4" });
    const url = URL.createObjectURL(blob);

    //setOutputUrl(url);
    //setProcessing(false);

    console.log(url);
    trimmedBlobRef.current = blob;
  };

  const handleSend = async () => {
    const duration: number = range[1] - range[0];

    if (duration <= UtilityHelper.defaultAudioMaxSeconds) {
      Loader.modal();
      await exportTrim();
      Loader.modal(false);

      //console.log(trimmedBlobRef.current!);
      if (trimmedBlobRef.current != null) {
        // Convert file to Base64
        UtilityHelper.blobToBase64(trimmedBlobRef.current!)
          .then(async (base64String) => {
            //console.log(base64String);
            //setBase64(base64String);
            dispatch(
              provideFileEdited({
                fileString: base64String,
                fromAction: fileStringDetailSelector?.fromAction!,
                type: "video",
                durationSeconds: range[1],
              })
            );

            await UtilityHelper.delay(500);
            dispatch(removeModal(ModalKeys.video_editor));
          })
          .catch((error) => {
            console.error("Error converting file to Base64:", error);
          });
      }
    } else {
      ShowToast.error(AppTranslation.text("video_limit_exceeded"));
    }
  };

  return (
    <div
      className="h-100 video-trimmer"
      style={{
        display: "grid",
        gridTemplateRows: `1fr ${thumbnails.length === 0 ? 100 : 200}px`,
      }}
    >
      <div className="d-center bgcolor-black position-relative py-5">
        {/* {base64 != null && (
          <video controls style={{ width: 200 }}>
            <source src={base64!} type={"video/mp4"} />
            Your browser does not support the video tag.
          </video>
        )} */}

        <video
          ref={videoRef}
          src={src}
          controls={false}
          playsInline
          muted
          style={{ width: "100%", maxHeight: type === 3 ? "40vh" : "55vh" }}
        />

        <div className="video-play-icon">
          <div className="icon-play" role="button" onClick={playTrim}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="30"
              height="30"
              fill="currentColor"
              className="bi bi-play-fill"
              viewBox="0 0 16 16"
            >
              <path d="m11.596 8.697-6.363 3.692c-.54.313-1.233-.066-1.233-.697V4.308c0-.63.692-1.01 1.233-.696l6.363 3.692a.802.802 0 0 1 0 1.393" />
            </svg>
          </div>
        </div>
      </div>

      <div className={`position-relative px-${type === 3 ? 0 : 3} pt-3`}>
        {thumbnails.length > 0 && (
          <ThumbnailRangeSlider
            thumbnails={thumbnails}
            width={`100%`}
            height={80}
            maxDuration={duration}
            onChange={(start, endd) => setRange([start, endd])}
          />
        )}

        <div className="d-flex align-items-center justify-content-end pt-3">
          {/* <button onClick={playTrim} style={{ marginTop: 20, marginRight: 10 }}>
            ▶ Play Trim Preview
          </button> */}

          {/* <button disabled={processing} onClick={exportTrim}>
            {processing ? "Processing..." : "💾 Export Trimmed Video"}
          </button> */}

          <IconCircleFilledIcon
            size={45}
            name="paper_plane"
            color="white"
            bgColor="bgcolor-primary"
            handleClick={() => handleSend()}
          />
        </div>

        {/* {outputUrl && (
          <div style={{ marginTop: 20 }}>
            <h4>Trimmed Output:</h4>
            <video
              src={outputUrl}
              controls
              playsInline
              muted
              style={{ width: "100%" }}
            />
            <a href={outputUrl} download="trimmed.mp4">
              ⬇ Download MP4
            </a>
          </div>
        )} */}

        <canvas
          ref={canvasRef}
          width={200}
          height={100}
          style={{ display: "none" }}
        />
      </div>
    </div>
  );
};

///////////////////////////////////////////////////////////////////////////////
interface ThumbnailRangeSliderProps {
  thumbnails: string[]; // array of thumbnail URLs
  width?: number | string;
  height?: number | string;
  maxDuration: number;
  onChange?: (start: number, end: number) => void; // start/end in 0..1
}

const ThumbnailRangeSlider: React.FC<ThumbnailRangeSliderProps> = ({
  thumbnails,
  width = 600,
  height = 80,
  maxDuration = 1,
  onChange,
}) => {
  const lang: string | "en" | "ar" = CacheStorage.getValue(
    StorageKeys.language
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const [start, setStart] = useState(0); // 0..1
  const [end, setEnd] = useState(1); // 0..1
  const [dragging, setDragging] = useState<"start" | "end" | null>(null);

  const handleMouseDown =
    (handle: "start" | "end") => (e: React.MouseEvent) => {
      e.preventDefault();
      setDragging(handle);
    };

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragging || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    let pos = (e.clientX - rect.left) / rect.width;
    pos = Math.max(0, Math.min(1, pos));

    if (dragging === "start") {
      setStart(Math.min(pos, end - 0.01));
      onChange?.(Math.min(pos, end - 0.01) * maxDuration, end * maxDuration);
    } else if (dragging === "end") {
      setEnd(Math.max(pos, start + 0.01));
      onChange?.(
        start * maxDuration,
        Math.max(pos, start + 0.01) * maxDuration
      );
    }
  };

  const handleMouseUp = () => setDragging(null);

  const touchMoveHandler = (e: TouchEvent) => {
    // Prevent default behavior to avoid page scrolling or other unwanted actions during drag
    e.preventDefault();

    if (!dragging || !containerRef.current) return;

    // Get the bounding rectangle of the container
    const rect = containerRef.current.getBoundingClientRect();

    // Get the touch position (use the first touch point)
    const touchX = e.touches[0].clientX;

    // Calculate the position as a percentage of the container width
    let pos = (touchX - rect.left) / rect.width;
    pos = Math.max(0, Math.min(1, pos)); // Clamp position between 0 and 1

    // Handle the dragging based on whether it's the start or end
    if (dragging === "start") {
      // Set the start position and call the onChange callback
      setStart(Math.min(pos, end - 0.01));
      onChange?.(Math.min(pos, end - 0.01) * maxDuration, end * maxDuration);
    } else if (dragging === "end") {
      // Set the end position and call the onChange callback
      setEnd(Math.max(pos, start + 0.01));
      onChange?.(
        start * maxDuration,
        Math.max(pos, start + 0.01) * maxDuration
      );
    }
  };

  const handleTouchStart = (handle: "start" | "end") => {
    //e.preventDefault();
    setDragging(handle);
  };

  const handleTouchEnd = (e: TouchEvent) => setDragging(null);

  useEffect(() => {
    // Attach event listeners for both mouse and touch events
    window.addEventListener("touchmove", touchMoveHandler); //, { passive: true });
    window.addEventListener("touchend", handleTouchEnd);

    // Attach the mousemove and touchmove events
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      // Cleanup listeners when component is unmounted
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);

      window.removeEventListener("touchmove", touchMoveHandler);
      window.removeEventListener("touchend", handleTouchEnd);
    };
    // eslint-disable-next-line
  }, [dragging, start, end]);

  return (
    <div>
      {thumbnails.length === 0 ? (
        <Center className="pt-3">{Loader.normal()}</Center>
      ) : (
        <>
          <div
            ref={containerRef}
            style={{
              position: "relative",
              width,
              height,
              display: "flex",
              userSelect: "none",
              overflow: "hidden",
              borderRadius: 6,
              border: `1px solid ${AppTheme.grey}`,
            }}
          >
            {/* THUMBNAILS */}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${thumbnails.length}, 1fr)`,
                gap: 0,
              }}
            >
              {thumbnails.map((t, i) => (
                <img
                  key={i}
                  src={t}
                  style={{
                    width: "100%",
                    height: height,
                    objectFit: "cover",
                    //borderRadius: 3,
                  }}
                  alt="preview"
                />
              ))}
            </div>

            {/* OVERLAY RANGE */}
            <div
              style={{
                position: "absolute",
                left: `${start * 100}%`,
                width: `${(end - start) * 100}%`,
                top: 0,
                bottom: 0,
                border: `2px solid ${AppTheme.primary}`,
                boxSizing: "border-box",
                backgroundColor: "rgba(0,0,255,0.15)",
                borderRadius: 5,
              }}
            >
              {/* LEFT HANDLE */}
              <div
                onMouseDown={handleMouseDown("start")}
                onTouchStart={() => handleTouchStart("start")}
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 8,
                  cursor: "ew-resize",
                  backgroundColor: AppTheme.primary,
                }}
              />

              {/* RIGHT HANDLE */}
              <div
                onMouseDown={handleMouseDown("end")}
                onTouchStart={() => handleTouchStart("end")}
                style={{
                  position: "absolute",
                  right: 0,
                  top: 0,
                  bottom: 0,
                  width: 8,
                  cursor: "ew-resize",
                  backgroundColor: AppTheme.primary,
                }}
              />
            </div>
          </div>
          <div className="font-size-12_14">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${thumbnails.length}, 1fr)`,
                gap: 0,
                direction: lang === "en" ? "rtl" : "ltr",
                textAlign: lang === "en" ? "left" : "right",
              }}
            >
              {thumbnails.map(
                (t, i) =>
                  i > 0 && (
                    <span key={i}>
                      {(i === 0 ? 0 : maxDuration / i).toFixed(1)}s
                    </span>
                  )
              )}
              <span>0</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

///////////////////////////////////////////////////////////////////////////////
const VideoTrimmerScreen = (props: { screenMobileView: boolean }) => {
  const dispatch = useDispatch();

  const fileStringDetailSelector = useSelector(
    (state: RootState) => state.imageEditor.fileStringDetail
  );

  const [videoFile, setVideoFile] = useState<string | null>(null);

  useEffect(() => {
    if (fileStringDetailSelector?.fileString != null) {
      setVideoFile(fileStringDetailSelector?.fileString);
    }

    return () => {
      dispatch(provideFileString(null));
    };
    // eslint-disable-next-line
  }, [fileStringDetailSelector]);

  const bodyContent: JSX.Element = (
    <div className="h-100 overflow-hidden">
      {videoFile && <VideoTrimmer src={videoFile} />}
    </div>
  );

  return (
    <>
      {props.screenMobileView === true ? (
        <Scaffold appBarTitle={AppTranslation.text("video")} isModal={true}>
          {bodyContent}
        </Scaffold>
      ) : (
        bodyContent
      )}
    </>
  );
};

export default VideoTrimmerScreen;
